// erp-core internal/license (node mode). Mounted outside the auth middleware:
// activation happens before anyone can sign in on an unlicensed node.
//
//   GET  /v1/erp/license/status   → LicenceStatus (200 on a node; 404 on cloud,
//                                   where the module is not mounted)
//   POST /v1/erp/license/activate {activation_code} | {file} → LicenceStatus | problem:
//        400 license_malformed / license_signature_invalid / invalid_json
//        409 license_wrong_node / license_business_mismatch / license_expired
//        422 activation_code_invalid · 409 activation_code_used / activation_code_expired
//        503 activation_code_unavailable / license_key_missing
//        401/403 when a VALID licence is being replaced by someone who is not the owner.
//   GET  /v1/erp/license/qr       → LicenceQr (the QR the owner's phone scans; the
//                                   phone then POSTs /license/claim to this node)
// activate and qr are public while the node is unlicensed, owner-only after.
//
// erp-core internal/auth/local (node mode, both PUBLIC):
//   GET  /v1/erp/auth/status     → { mode:'node', bootstrap_required, idle_lock_seconds }
//                                  (404 on cloud: the local strategy is not mounted)
//   POST /v1/erp/auth/bootstrap  { code, phone, name, pin, business_name?, device? }
//        → 201 SessionResponse (the owner is signed in) | problem:
//        401 invalid_bootstrap_code · 409 already_bootstrapped /
//        license_business_mismatch · 422 errors[{field: phone|name|pin|business_id}]

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi, isApiError } from '@/lib/api';
import type { LocalSessionResponse } from '@/lib/auth/local';
import { ME_QUERY_KEY } from '@/lib/me';
import { activationBody } from '../lib/code';

/** The activation paths a node offers (license.StatusResponse.activation_methods). */
export type ActivationMethod = 'code' | 'qr' | 'file';

/** erp-core license.StatusResponse (snake_case, exactly as sent). */
export interface LicenceStatus {
  licensed: boolean;
  /** Why not licensed: missing | invalid | expired | no_key …; null when licensed. */
  reason: string | null;
  node_id: string;
  business_id: string | null;
  licensee: string | null;
  modules: string[];
  issued_at: string | null;
  expires_at: string | null;
  /** The device line «الجهاز: استقبال-١ · ويندوز · v1.5.0»; null when the node has none. */
  device_name: string | null;
  /** Go GOOS key (windows | linux | darwin …); '' when unknown. */
  os: string;
  /** erp-core build version (shared/buildinfo); '' when unknown. */
  app_version: string;
  /**
   * What this node can take now: `code` only when a redemption service is
   * wired; `qr` and `file` whenever a verification key is configured; [] with
   * reason `no_key`. The screen offers exactly these (rule 6).
   */
  activation_methods: ActivationMethod[];
}

/** erp-core license.QRResponse — GET /v1/erp/license/qr. */
export interface LicenceQr {
  node_id: string;
  nonce: string;
  expires_at: string;
  /** When the screen must ask for a new QR (the nonce rotates). */
  refresh_at: string;
  /** The text the QR encodes: `ehjizli://node/activate?v=1&node_id=…&nonce=…&addr=…`. */
  qr_payload: string;
  device_name: string | null;
  os: string;
  app_version: string;
}

export const LICENCE_STATUS_KEY = ['erp', 'license', 'status'] as const;
export const LICENCE_QR_KEY = ['erp', 'license', 'qr'] as const;

/** While the QR is on screen the status is re-read this often: the phone's claim licenses the node. */
export const CLAIM_POLL_MS = 2_000;
/** Never ask for a new QR more often than this, whatever `refresh_at` says. */
export const QR_MIN_REFRESH_MS = 1_000;
/**
 * Never wait longer than erp-core's claim TTL (license.ClaimTTL, 5 min): a
 * re-read before `refresh_at` returns the same nonce. Also keeps the timer
 * inside setTimeout's range (a far `refresh_at` would overflow and fire at once).
 */
export const QR_MAX_REFRESH_MS = 5 * 60_000;

/** The methods a status offers; [] when the server sent none. */
export function activationMethods(st: LicenceStatus | undefined): ActivationMethod[] {
  return Array.isArray(st?.activation_methods) ? st.activation_methods : [];
}

/** Public read: no bearer recovery, no X-Business-Id (nobody is signed in yet). */
export function fetchLicenceStatus(): Promise<LicenceStatus> {
  return erpApi.get<LicenceStatus>('/license/status', undefined, { businessId: null, authRecovery: false });
}

export function useLicenceStatus(enabled = true, pollMs: number | false = false) {
  return useQuery({
    queryKey: LICENCE_STATUS_KEY,
    queryFn: fetchLicenceStatus,
    enabled,
    staleTime: 30_000,
    retry: false,
    refetchInterval: pollMs,
  });
}

/**
 * The QR. Public while unlicensed; a licensed node's owner renews with the
 * session (server: owner gate), so the bearer and business header are kept
 * like activate.
 */
export function fetchLicenceQr(): Promise<LicenceQr> {
  return erpApi.get<LicenceQr>('/license/qr', undefined, { authRecovery: false });
}

/** Milliseconds until the QR must be re-read (`refresh_at`), clamped to [QR_MIN_REFRESH_MS, QR_MAX_REFRESH_MS]. */
export function qrRefreshDelay(qr: Pick<LicenceQr, 'refresh_at'> | undefined, now = Date.now()): number | false {
  if (!qr) return false;
  const at = Date.parse(qr.refresh_at);
  if (Number.isNaN(at)) return false;
  return Math.min(QR_MAX_REFRESH_MS, Math.max(QR_MIN_REFRESH_MS, at - now));
}

export function useLicenceQr(enabled: boolean) {
  return useQuery({
    queryKey: LICENCE_QR_KEY,
    queryFn: fetchLicenceQr,
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => qrRefreshDelay(query.state.data),
  });
}

export function activateLicence(value: string): Promise<LicenceStatus> {
  // A licensed node's owner renews with the session (server: owner gate), so
  // the bearer is kept.
  return erpApi.post<LicenceStatus>('/license/activate', activationBody(value), { authRecovery: false });
}

export function useActivateLicence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: activateLicence,
    onSuccess: (status) => {
      qc.setQueryData(LICENCE_STATUS_KEY, status);
      // /me carries node.licensed + the licence's flags.
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

export type ActivationErrorKind = 'invalid' | 'used' | 'expired' | 'offline' | 'unavailable' | 'refused' | 'failed';

export interface ActivationError {
  kind: ActivationErrorKind;
  message: string;
}

/** Copy for each refusal (board states implied: invalid / used / expired / offline). */
export const ACTIVATION_COPY = {
  invalid: 'رمز التفعيل غير صحيح.',
  fileInvalid: 'ملف التفعيل غير صالح.',
  used: 'هذا الرمز مستخدم على جهاز آخر.',
  otherBusiness: 'هذا الرمز لنشاط غير النشاط المسجَّل على هذا الجهاز.',
  expired: 'انتهت صلاحية رمز التفعيل.',
  offline: 'لم تنجح المحاولة. تحقّق من الاتصال ثم أعد المحاولة.',
  /** erp-core's own detail for 503 activation_code_unavailable (fallback when the problem has none). */
  unavailable: 'التفعيل بالرمز يحتاج اتصالاً بخدمة احجزلي، وهو غير متاح الآن. امسح الرمز من تطبيق احجزلي للأعمال أو استورد ملف التفعيل',
  /** erp-core's own detail for license_key_missing (status reason no_key: no path is offered). */
  keyMissing: 'لم يُضبط مفتاح التحقق من التراخيص على هذا الجهاز',
  refused: 'لا يمكن تنفيذ هذا الإجراء.',
  failed: 'لم تنجح المحاولة.',
  licenceExpired: 'انتهى ترخيص هذا الجهاز.',
  activated: 'تم تفعيل هذا الجهاز. سجّل الدخول للمتابعة.',
  activatedFirstRun: 'تم تفعيل هذا الجهاز. أنشئ حساب المالك للمتابعة.',
} as const;

/** Map an activation failure to its state + sentence. */
export function activationError(err: unknown): ActivationError {
  if (!isApiError(err)) return { kind: 'failed', message: ACTIVATION_COPY.failed };
  if (err.isNetwork) return { kind: 'offline', message: ACTIVATION_COPY.offline };
  switch (err.code) {
    case 'activation_code_invalid':
      return { kind: 'invalid', message: ACTIVATION_COPY.invalid };
    case 'license_malformed':
    case 'license_signature_invalid':
    case 'invalid_json':
      return { kind: 'invalid', message: ACTIVATION_COPY.fileInvalid };
    case 'activation_code_used':
    case 'license_wrong_node':
      return { kind: 'used', message: ACTIVATION_COPY.used };
    case 'license_business_mismatch':
      return { kind: 'used', message: ACTIVATION_COPY.otherBusiness };
    case 'activation_code_expired':
    case 'license_expired':
      return { kind: 'expired', message: ACTIVATION_COPY.expired };
    case 'activation_code_unavailable':
      return { kind: 'unavailable', message: err.detail || ACTIVATION_COPY.unavailable };
  }
  if (err.status === 400 || err.status === 422) return { kind: 'invalid', message: ACTIVATION_COPY.invalid };
  if (err.status === 401 || err.status === 403) return { kind: 'refused', message: err.detail || ACTIVATION_COPY.refused };
  return { kind: 'failed', message: err.detail || ACTIVATION_COPY.failed };
}

/** 404 = erp-core runs in cloud mode (no licence module): activation does not apply. */
export function isNotANode(err: unknown): boolean {
  return isApiError(err) && err.status === 404;
}

// ------------------------------------------------------- node first run --

/** erp-core GET /v1/erp/auth/status (node only). */
export interface NodeAuthStatus {
  mode: string;
  /** No local user exists yet: the owner must be created with the one-time code. */
  bootstrap_required: boolean;
  idle_lock_seconds: number;
}

export const AUTH_STATUS_KEY = ['erp', 'auth', 'status'] as const;

export function fetchNodeAuthStatus(): Promise<NodeAuthStatus> {
  return erpApi.get<NodeAuthStatus>('/auth/status', undefined, { businessId: null, authRecovery: false });
}

export function useNodeAuthStatus(enabled = true) {
  return useQuery({
    queryKey: AUTH_STATUS_KEY,
    queryFn: fetchNodeAuthStatus,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** POST /v1/erp/auth/bootstrap body (phone in E.164, PIN ASCII digits). */
export interface BootstrapInput {
  code: string;
  phone: string;
  name: string;
  pin: string;
  /** Omitted when the node is licensed: erp-core then uses the licence's business + licensee. */
  business_name?: string;
}

export function bootstrapOwner(input: BootstrapInput): Promise<LocalSessionResponse> {
  return erpApi.post<LocalSessionResponse>('/auth/bootstrap', input, { businessId: null, authRecovery: false });
}

/** Copy for the first-run screen's refusals (states not drawn — PROPOSED, see OPEN-QUESTIONS). */
export const FIRST_RUN_COPY = {
  codeInvalid: 'رمز الإعداد الأول غير صحيح.',
  codeIncomplete: 'رمز الإعداد الأول ١٢ حرفاً ورقماً.',
  already: 'تم إنشاء حساب المالك على هذا الجهاز مسبقاً. سجّل الدخول للمتابعة.',
  nameRequired: 'الاسم مطلوب.',
  businessRequired: 'اسم النشاط مطلوب.',
  phoneInvalid: 'رقم الهاتف غير صحيح — يجب أن يبدأ بـ 07',
  pinFormat: 'الرمز السري ستة أرقام.',
  pinWeak: 'هذا الرمز سهل التخمين. اختر رمزاً آخر.',
  pinMismatch: 'الرمزان غير متطابقين.',
  offline: 'لم تنجح المحاولة. تحقّق من الاتصال ثم أعد المحاولة.',
  failed: 'لم تنجح المحاولة.',
} as const;

export type FirstRunField = 'code' | 'business_name' | 'name' | 'phone' | 'pin';

export interface FirstRunError {
  /** The field the refusal belongs to; null = a page-level message. */
  field: FirstRunField | null;
  message: string;
  /** 409 already_bootstrapped: the owner exists — go and sign in. */
  already?: boolean;
}

/** Map a bootstrap refusal to its field + sentence (server detail/messages when it sent them). */
export function firstRunError(err: unknown): FirstRunError {
  if (!isApiError(err)) return { field: null, message: FIRST_RUN_COPY.failed };
  if (err.isNetwork) return { field: null, message: FIRST_RUN_COPY.offline };
  if (err.code === 'invalid_bootstrap_code') return { field: 'code', message: FIRST_RUN_COPY.codeInvalid };
  if (err.code === 'already_bootstrapped') return { field: null, message: FIRST_RUN_COPY.already, already: true };
  const fe = err.errors?.[0];
  if (fe) {
    const field: FirstRunField | null =
      fe.field === 'phone' || fe.field === 'name' || fe.field === 'pin' || fe.field === 'business_name' ? fe.field : null;
    if (field === 'pin' && fe.code === 'weak_pin') return { field, message: FIRST_RUN_COPY.pinWeak };
    return { field, message: fe.message || err.detail || FIRST_RUN_COPY.failed };
  }
  return { field: null, message: err.detail || FIRST_RUN_COPY.failed };
}
